/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import {
  getAllReferrals,
  changeReferral,
  removeUserReferrer,
  CreateReferral,
  getAllReferralCodes,
} from "../../api/admin";
import Card from "../../components/shared/Card";
import Swal from "sweetalert2";
import { PuffLoader } from "react-spinners";
import Button from "../../components/shared/Button";
import { FaSearch, FaChevronDown, FaUserSlash } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../permissions/permissions";

const AllReferrals = () => {
  const { hasPermission } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferralCode, setSelectedReferralCode] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    eventType: "Any",
    dateFrom: "",
    dateTo: "",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    fromRecord: 0,
    toRecord: 0,
    totalRecords: 0,
  });

  const [pageSize, setPageSize] = useState(10);
  const [showPopup, setShowPopup] = useState(false);
  const [showChangePopup, setShowChangePopup] = useState(false);
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    owner: "",
    email: "",
    whatsapp: "",
    ref: "",
  });
  const [changeFormData, setChangeFormData] = useState({
    email: "",
    referral_code: "",
  });
  const [removeFormData, setRemoveFormData] = useState({ email: "" });

  // Estados para el autocompletado
  const [referralCodeOptions, setReferralCodeOptions] = useState([]);
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  const [loadingReferralCodes, setLoadingReferralCodes] = useState(false);
  const [referralCodeSearch, setReferralCodeSearch] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [emailSearchTerm, setEmailSearchTerm] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [changeFormErrors, setChangeFormErrors] = useState({});
  const [removeFormErrors, setRemoveFormErrors] = useState({});
  const [removingReferrer, setRemovingReferrer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [changingReferral, setChangingReferral] = useState(false);

  // Refs para manejar el dropdown
  const referralCodeInputRef = useRef(null);
  const referralCodeDropdownRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const loadReferrals = async (page = 1, name = "", email = "") => {
    try {
      setLoading(true);
      const response = await getAllReferrals(page, pageSize, name, email);
      setReferrals(response.data || []);

      if (response.meta) {
        setPagination({
          currentPage: response.meta.current_page || 1,
          lastPage: response.meta.last_page || 1,
          fromRecord: response.meta.from_record || 0,
          toRecord: response.meta.to_record || 0,
          totalRecords: response.meta.total_records || 0,
        });
      } else {
        setPagination({
          currentPage: 1,
          lastPage: 1,
          fromRecord: referrals.length > 0 ? 1 : 0,
          toRecord: referrals.length,
          totalRecords: referrals.length,
        });
      }
    } catch (error) {
      console.error("Error loading referrals:", error);
      setReferrals([]);
      setPagination({
        currentPage: 1,
        lastPage: 1,
        fromRecord: 0,
        toRecord: 0,
        totalRecords: 0,
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "Error al cargar los referidos",
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar códigos de referido con debounce
  const loadReferralCodes = async (searchCode = "") => {
    try {
      setLoadingReferralCodes(true);
      const response = await getAllReferralCodes(1, 10, searchCode);
      setReferralCodeOptions(response.data || []);
    } catch (error) {
      console.error("Error loading referral codes:", error);
      setReferralCodeOptions([]);
    } finally {
      setLoadingReferralCodes(false);
    }
  };

  // Debounce para la búsqueda de códigos de referido
  const debouncedSearchReferralCodes = (searchValue) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      loadReferralCodes(searchValue);
    }, 300);
  };

  // Manejar cambio en el input de código de referido
  const handleReferralCodeInputChange = (e) => {
    const value = e.target.value;
    setReferralCodeSearch(value);

    setSelectedReferralCode(null);

    setChangeFormData((prev) => ({
      ...prev,
      referral_code: "",
    }));

    if (changeFormErrors.referral_code) {
      setChangeFormErrors((prev) => ({
        ...prev,
        referral_code: "",
      }));
    }

    setShowReferralDropdown(true);
    debouncedSearchReferralCodes(value);
  };

  // Manejar selección de una opción del dropdown
  const handleReferralCodeSelect = (selectedOption) => {
    const codeValue = selectedOption.referral_code || selectedOption.code;

    setReferralCodeSearch(codeValue);
    setSelectedReferralCode(selectedOption);
    setChangeFormData((prev) => ({
      ...prev,
      referral_code: codeValue,
    }));
    setShowReferralDropdown(false);

    if (changeFormErrors.referral_code) {
      setChangeFormErrors((prev) => ({
        ...prev,
        referral_code: "",
      }));
    }
  };

  // Manejar focus del input
  const handleReferralCodeFocus = () => {
    setShowReferralDropdown(true);
    if (referralCodeOptions.length === 0) {
      loadReferralCodes(referralCodeSearch);
    }
  };

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        referralCodeDropdownRef.current &&
        !referralCodeDropdownRef.current.contains(event.target) &&
        referralCodeInputRef.current &&
        !referralCodeInputRef.current.contains(event.target)
      ) {
        setShowReferralDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadReferrals(1, searchTerm, emailSearchTerm);
  }, []);

  useEffect(() => {
    if (!loading) {
      loadReferrals(1, searchTerm, emailSearchTerm);
    }
  }, [pageSize]);

  // Función para cambiar de página
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.lastPage || loading) return;
    loadReferrals(page, searchTerm, emailSearchTerm);
  };

  // Función para cambiar el tamaño de página
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    if (newSize !== pageSize) {
      setPageSize(newSize);
    }
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setFormData({
      name: "",
      owner: "",
      email: "",
      whatsapp: "",
      ref: "",
    });
    setFormErrors({});
  };

  // popup de cambio de referido
  const handleOpenChangePopup = () => {
    setShowChangePopup(true);
  };

  const handleCloseChangePopup = () => {
    setShowChangePopup(false);
    setChangeFormData({
      email: "",
      referral_code: "",
    });
    setChangeFormErrors({});
    setReferralCodeSearch("");
    setSelectedReferralCode(null);
    setReferralCodeOptions([]);
    setShowReferralDropdown(false);
  };

  const handleChangeInputChange = (e) => {
    const { name, value } = e.target;
    setChangeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (changeFormErrors[name]) {
      setChangeFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Manejar el cambio en el input de búsqueda
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Manejar el cambio en el input de búsqueda por correo
  const handleEmailSearchInputChange = (e) => {
    setEmailSearchTerm(e.target.value);
  };

  const validateChangeForm = () => {
    const errors = {};

    if (!changeFormData.email.trim()) {
      errors.email = "El correo electrónico es requerido";
    } else if (!/\S+@\S+\.\S+/.test(changeFormData.email)) {
      errors.email = "El correo electrónico no es válido";
    }

    // Validar que se haya seleccionado un código válido
    if (!selectedReferralCode || !changeFormData.referral_code.trim()) {
      errors.referral_code =
        "Debe seleccionar un código de referido válido de la lista";
    }

    setChangeFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangeSubmit = async (e) => {
    e.preventDefault();

    if (!validateChangeForm()) {
      return;
    }

    setChangingReferral(true);

    try {
      await changeReferral(changeFormData);

      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "Referido cambiado correctamente",
      });

      loadReferrals(pagination.currentPage, searchTerm, emailSearchTerm);
      handleCloseChangePopup();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.title || "Error al cambiar el referido",
      });
    } finally {
      setChangingReferral(false);
    }
  };

  // popup de eliminación de referente
  const handleOpenRemovePopup = () => {
    setShowRemovePopup(true);
  };

  const handleCloseRemovePopup = () => {
    setShowRemovePopup(false);
    setRemoveFormData({ email: "" });
    setRemoveFormErrors({});
  };

  const handleRemoveInputChange = (e) => {
    const { name, value } = e.target;
    setRemoveFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (removeFormErrors[name]) {
      setRemoveFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateRemoveForm = () => {
    const errors = {};

    if (!removeFormData.email.trim()) {
      errors.email = "El correo electrónico es requerido";
    } else if (!/\S+@\S+\.\S+/.test(removeFormData.email)) {
      errors.email = "El correo electrónico no es válido";
    }

    setRemoveFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRemoveSubmit = async (e) => {
    e.preventDefault();

    if (!validateRemoveForm()) {
      return;
    }

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar referente?",
      html: `El usuario <strong>${removeFormData.email}</strong> quedará sin líder de comunidad. Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setRemovingReferrer(true);

    try {
      await removeUserReferrer({ email: removeFormData.email.trim() });

      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "Referente eliminado correctamente",
      });

      loadReferrals(pagination.currentPage, searchTerm, emailSearchTerm);
      handleCloseRemovePopup();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.title || "Error al eliminar el referente",
      });
    } finally {
      setRemovingReferrer(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "El nombre del workspace es requerido";
    }

    if (!formData.owner.trim()) {
      errors.owner = "El nombre del propietario es requerido";
    }

    if (!formData.email.trim()) {
      errors.email = "El correo electrónico es requerido";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "El correo electrónico no es válido";
    }

    if (!formData.whatsapp.trim()) {
      errors.whatsapp = "El número de WhatsApp es requerido";
    } else if (!/^\d+$/.test(formData.whatsapp)) {
      errors.whatsapp = "Ingrese solo números";
    }

    if (!formData.ref.trim()) {
      errors.ref = "El código de referido es requerido";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const requestData = {
        referee: {
          workspace_name: formData.name,
          name: formData.owner,
          email: formData.email,
          whatsapp: formData.whatsapp,
        },
        terms_conditions: true,
        ref: formData.ref,
      };

      await CreateReferral(requestData);

      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "Referido creado correctamente",
      });

      loadReferrals(pagination.currentPage, searchTerm, emailSearchTerm);
      handleClosePopup();
      setSubmitting(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "Error al crear el referido",
      });
      setSubmitting(false);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const { currentPage, lastPage } = pagination;

    if (lastPage >= 1) {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(lastPage, startPage + 4);

      if (endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
      }

      if (startPage > 1) {
        pages.push(
          <button
            key="1"
            className={`pagination__button ${
              currentPage === 1 ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(1)}
            disabled={loading}
          >
            1
          </button>,
        );
        if (startPage > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination__ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`pagination__button ${
              currentPage === i ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(i)}
            disabled={loading}
          >
            {i}
          </button>,
        );
      }

      if (endPage < lastPage) {
        if (endPage < lastPage - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination__ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={lastPage}
            className={`pagination__button ${
              currentPage === lastPage ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(lastPage)}
            disabled={loading}
          >
            {lastPage}
          </button>,
        );
      }
    }

    return (
      <div className="pagination">
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          &laquo;
        </button>
        {pages}
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === lastPage || loading}
        >
          &raquo;
        </button>
      </div>
    );
  };

  const renderPageSizeSelector = () => {
    return (
      <div className="pagination__size-selector">
        <label htmlFor="pageSize" className="pagination__size-label">
          Mostrar:
        </label>
        <select
          id="pageSize"
          className="filter-select pagination__size-select"
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={loading}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>
    );
  };

  // Realizar la búsqueda cuando se hace clic en el botón
  const handleSearch = () => {
    loadReferrals(1, searchTerm, emailSearchTerm);
  };

  // También realizar la búsqueda cuando se presiona Enter en el campo de búsqueda
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  return (
    <Card className={"all-referrals-card"}>
      <div className="all-referrals">
        <div className="all-referrals__header">
          <h2 className="all-referrals__title">Listado de Referidos</h2>
          <div className="all-referrals__actions">
            {hasPermission(PERMISSIONS.CREATE_REFERRAL) && (
              <Button onClick={handleOpenPopup} variant="primary">
                Generar Nuevo Referido
              </Button>
            )}
            {hasPermission(PERMISSIONS.CHANGE_REFERRAL) && (
              <Button onClick={handleOpenChangePopup} variant="outline">
                Cambiar Referido
              </Button>
            )}
            {hasPermission(PERMISSIONS.REMOVE_REFERRER) && (
              <Button onClick={handleOpenRemovePopup} variant="outline--delete">
                <FaUserSlash />
                Eliminar Referente
              </Button>
            )}
          </div>
        </div>

        <div className="all-referrals__toolbar">
          <div className="all-referrals__search">
            <div className="input-group__search-container">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                className="input-group__input-search"
                placeholder="Buscar por nombre"
              />
              <button
                className="input-group__search-button"
                onClick={handleSearch}
              >
                <FaSearch />
              </button>
            </div>
            <div className="input-group__search-container">
              <input
                type="text"
                value={emailSearchTerm}
                onChange={handleEmailSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                className="input-group__input-search"
                placeholder="Buscar por correo"
              />
              <button
                className="input-group__search-button"
                onClick={handleSearch}
              >
                <FaSearch />
              </button>
            </div>
          </div>

          <div className="all-referrals__filters">{renderPageSizeSelector()}</div>
        </div>

        <div className="all-referrals__table">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Código de Referido</th>
                <th>Referente Nombre</th>
              </tr>
            </thead>
            <tbody>
              {referrals.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-data">
                    No hay referidos registrados
                  </td>
                </tr>
              ) : (
                referrals.map((referral, index) => (
                  <tr key={index}>
                    <td>{referral.name || "Sin nombre"}</td>
                    <td>{referral.email || "Sin correo"}</td>
                    <td>{referral.referral_code || "-"}</td>
                    <td>{referral.referrer_name || "Sin nombre"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Componente de paginación */}
        <div className="all-referrals__pagination">
          <div className="pagination__container">
            <div>{renderPagination()}</div>
            <div className="pagination__info">
              Mostrando {pagination.fromRecord} a {pagination.toRecord} de{" "}
              {pagination.totalRecords} registros
            </div>
          </div>
        </div>
      </div>

      {/* Popup para creación de referido */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h3>Generar Nuevo Referido</h3>
              <button className="popup-close" onClick={handleClosePopup}>
                &times;
              </button>
            </div>
            <div className="popup-content">
              <form onSubmit={handleSubmit}>
                <div className="input-group">
                  <label className="input-group__label">
                    Nombre del Workspace{" "}
                    <span className="input-group__required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`input-group__input ${
                      formErrors.name ? "input-group__input--error" : ""
                    }`}
                  />
                  {formErrors.name && (
                    <span className="input-group__error">
                      {formErrors.name}
                    </span>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-group__label">
                    Nombre del Propietario{" "}
                    <span className="input-group__required">*</span>
                  </label>
                  <input
                    type="text"
                    name="owner"
                    value={formData.owner}
                    onChange={handleInputChange}
                    className={`input-group__input ${
                      formErrors.owner ? "input-group__input--error" : ""
                    }`}
                  />
                  {formErrors.owner && (
                    <span className="input-group__error">
                      {formErrors.owner}
                    </span>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-group__label">
                    Correo Electrónico{" "}
                    <span className="input-group__required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`input-group__input ${
                      formErrors.email ? "input-group__input--error" : ""
                    }`}
                  />
                  {formErrors.email && (
                    <span className="input-group__error">
                      {formErrors.email}
                    </span>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-group__label">
                    WhatsApp <span className="input-group__required">*</span>
                  </label>
                  <input
                    type="text"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleInputChange}
                    className={`input-group__input ${
                      formErrors.whatsapp ? "input-group__input--error" : ""
                    }`}
                  />
                  {formErrors.whatsapp && (
                    <span className="input-group__error">
                      {formErrors.whatsapp}
                    </span>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-group__label">
                    Código de Referido{" "}
                    <span className="input-group__required">*</span>
                  </label>
                  <input
                    type="text"
                    name="ref"
                    value={formData.ref}
                    onChange={handleInputChange}
                    className={`input-group__input ${
                      formErrors.ref ? "input-group__input--error" : ""
                    }`}
                  />
                  {formErrors.ref && (
                    <span className="input-group__error">{formErrors.ref}</span>
                  )}
                </div>

                <br />

                <div className="popup-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClosePopup}
                    disabled={submitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? "Generando..." : "Generar Referido"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Popup para cambio de referido */}
      {showChangePopup && (
        <div className="popup-overlay">
          <div className="popup-container-change-referral">
            <div className="popup-header">
              <h3>Cambiar Referido</h3>
              <button className="popup-close" onClick={handleCloseChangePopup}>
                &times;
              </button>
            </div>
            <div className="popup-content">
              <form className="popup-form" onSubmit={handleChangeSubmit}>
                <div>
                  <div className="input-group">
                    <label className="input-group__label">
                      Correo Electrónico{" "}
                      <span className="input-group__required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Correo del referido"
                      value={changeFormData.email}
                      onChange={handleChangeInputChange}
                      className={`input-group__input ${
                        changeFormErrors.email
                          ? "input-group__input--error"
                          : ""
                      }`}
                    />
                    {changeFormErrors.email && (
                      <span className="input-group__error">
                        {changeFormErrors.email}
                      </span>
                    )}
                  </div>

                  {/* Input con autocompletado para código de referido */}
                  <div className="input-group">
                    <label className="input-group__label">
                      Código de Referido al que se cambiará{" "}
                      <span className="input-group__required">*</span>
                    </label>
                    <div
                      className={`autocomplete-container ${
                        showReferralDropdown ? "active" : ""
                      }`}
                    >
                      <input
                        ref={referralCodeInputRef}
                        type="text"
                        name="referral_code"
                        placeholder="Escriba o seleccione un código de referido"
                        value={referralCodeSearch}
                        onChange={handleReferralCodeInputChange}
                        onFocus={handleReferralCodeFocus}
                        className={`input-group__input ${
                          changeFormErrors.referral_code
                            ? "input-group__input--error"
                            : ""
                        }`}
                        autoComplete="off"
                      />
                      <FaChevronDown
                        className={`autocomplete-icon ${
                          showReferralDropdown
                            ? "autocomplete-icon--rotated"
                            : ""
                        }`}
                        onClick={() =>
                          setShowReferralDropdown(!showReferralDropdown)
                        }
                      />

                      {/* Dropdown de opciones */}
                      {showReferralDropdown && (
                        <div
                          ref={referralCodeDropdownRef}
                          className="autocomplete-dropdown"
                        >
                          {loadingReferralCodes ? (
                            <div className="autocomplete-loading">
                              <span>Buscando códigos...</span>
                            </div>
                          ) : referralCodeOptions.length > 0 ? (
                            referralCodeOptions.map((option, index) => (
                              <div
                                key={index}
                                className={`autocomplete-option ${
                                  selectedReferralCode &&
                                  selectedReferralCode.referral_code ===
                                    (option.referral_code || option.code)
                                    ? "autocomplete-option--selected"
                                    : ""
                                }`}
                                onClick={() => handleReferralCodeSelect(option)}
                              >
                                <div className="option-code">
                                  {option.referral_code || option.code}
                                </div>
                                {option.name && (
                                  <div className="option-name">
                                    {option.name}
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="autocomplete-no-results">
                              {referralCodeSearch
                                ? `No se encontraron códigos que contengan "${referralCodeSearch}"`
                                : "No hay códigos de referido disponibles"}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {changeFormErrors.referral_code && (
                      <span className="input-group__error">
                        {changeFormErrors.referral_code}
                      </span>
                    )}
                  </div>
                </div>

                <div className="popup-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseChangePopup}
                    disabled={changingReferral}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={changingReferral}
                  >
                    {changingReferral ? "Cambiando..." : "Cambiar Referido"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Popup para eliminar el referente de un usuario */}
      {showRemovePopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h3>Eliminar Referente</h3>
              <button className="popup-close" onClick={handleCloseRemovePopup}>
                &times;
              </button>
            </div>
            <div className="popup-content">
              <form onSubmit={handleRemoveSubmit}>
                <p className="popup-note popup-note--warning">
                  El usuario quedará sin referente (líder de comunidad)
                  asignado.
                </p>

                <div className="input-group">
                  <label className="input-group__label">
                    Correo Electrónico{" "}
                    <span className="input-group__required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Correo del usuario"
                    value={removeFormData.email}
                    onChange={handleRemoveInputChange}
                    className={`input-group__input ${
                      removeFormErrors.email ? "input-group__input--error" : ""
                    }`}
                  />
                  {removeFormErrors.email && (
                    <span className="input-group__error">
                      {removeFormErrors.email}
                    </span>
                  )}
                </div>

                <div className="popup-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseRemovePopup}
                    disabled={removingReferrer}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-danger"
                    disabled={removingReferrer}
                  >
                    {removingReferrer ? "Eliminando..." : "Eliminar Referente"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AllReferrals;
